import { CareerPeriodData, CareerPointData, MemberInfoData, PEWorkData, SizeData, WorkData } from '../dto/EntityRepository';
import { AlertPosition, AlertType, CountryType, WorkType } from '../enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../constants/alertMessages';
import { getHintMessage } from '../constants/hintMessages';
import { cleanUpPrice } from '../util/renderingPrice';
import { INPUT_TAG_NAME_MAX_LENGTH } from '../constants/limits';
import { parseSupportedVideoUrl } from '../util/videoUrl';
import { parseSupportedTextBlockLinkUrl } from '../util/textBlockLinkUrl';

export const HANDLE_REGEX = /^(?=.{3,30}$)[a-z0-9]+(?:_[a-z0-9]+)*$/;
export const RESERVED_HANDLES = new Set([
  'architrave',
  'admin', 'auth', 'test', 
  'project', 'projects', 'work', 'works', 'text', 'texts', 'about', 'contact', 'settings', 'poll', 'index', 'indexes','sample', 'samples',
  'policy', 'privacy', 'terms',
  'error', 'onboard', 'example',

  'account', 'billing', 'payments',
  'support', 'help', 'docs', 'status', 'blog',
  'www', 'api', 'app', 'static', 'assets', 'cdn',
]);



export const useValidation = () => {
  const { enqueueAlert } = useStandardAlertStore();


  const isNumeric = (str: string): boolean => {
    return /^\d+$/.test(str);
  }

  const isValidYear = (value: string): boolean => {
    if (value === "") return true;
    if (!isNumeric(value)) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('ONLY_NUMBERS_ALLOWED'),
      });
      return false;
    }
    if (value.length > 4) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_YEAR_FORMAT'),
      });
      return false;
    }
    return true;
  }

  const isValidMonth = (value: string): boolean => {
    if (value === '') return true;
    if (!isNumeric(value)) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('ONLY_NUMBERS_ALLOWED'),
      });
      return false;
    }
    if (value === '0') return true;
    const month = Number(value);
    if (month < 1 || month > 12) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_MONTH_FORMAT'),
      });
      return false;
    }
    return true;
  };

  const isValidDay = (value: string): boolean => {
    if (value === '') return true;
    if (!isNumeric(value)) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('ONLY_NUMBERS_ALLOWED'),
      });
      return false;
    }
    if (value === '0') return true;
    const day = Number(value);
    if (day < 1 || day > 31) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_DAY_FORMAT'),
      });
      return false;
    }
    return true;
  };

  const isValidNumber = (value: string): boolean => {
    if (value === "") return true;
    const regex = /^[0-9]+(\.[0-9]?)?$/;
    return regex.test(value);
  }

  const isValidSizeField = (value: string): boolean => {
    if (!isValidNumber(value)) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_SIZE_FORMAT'),
      });
      return false;
    }
    return true;
  }


  const isValidSize = (value: SizeData): boolean => {
    const width = value.width ?? '';
    const height = value.height ?? '';
    const depth = value.depth ?? '';

    if (!isValidNumber(width) || !isValidNumber(height) || (depth && !isValidNumber(depth))) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_SIZE_FORMAT'),
        // content: "Invalid Size format.\nOnly positive numbers, including 0, with up to one decimal place are allowed.\n\nExamples of invalid: \n    9.99 => 2 decimals \n    9.999 => 3 decimals \n    -9 => negative numbers",
      });
      return false;
    }
    return true;
  };

  const isValidWorkType = (value: string): boolean => {
    const isValid = Object.values(WorkType).includes(value as WorkType);
    if (!isValid) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_WORK_TYPE'),
      });
      return false;
    }
    return true;
  };

  const isValidCountry = (value: string): boolean => {
    const isValid = Object.values(CountryType).includes(value as CountryType);
    if (!isValid) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('COUNTRY_NOT_SUPPORTED'),
      });
      return false;
    }
    return true;
  };

  const isValidPrice = (value: string): boolean => {
    if (value === "") return true;
    const price = cleanUpPrice(value);
    const regex = /^[0-9]+$/;
    const isValid = regex.test(price);
    if (!isValid) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_PRICE_FORMAT'),
      });
    }

    return isValid;
  };

  const checkType = (
    field:
      keyof SizeData |
      keyof WorkData |
      keyof PEWorkData |
      keyof CareerPointData |
      keyof CareerPeriodData |
      keyof MemberInfoData,
    value: string | SizeData): boolean | void => {
    switch (field) {
      case 'width':
      case 'height':
      case 'depth':
        if (!isValidSizeField(value as string)) {
          return;
        }
        break;
      case 'prodYear':
      case 'startYear':
      case 'endYear':
      case 'year':
        if (!isValidYear(value as string)) {
          return;
        }
        break;
      case 'startMonth':
      case 'endMonth':
        if (!isValidMonth(value as string)) {
          return;
        }
        break;
      case 'startDay':
      case 'endDay':
        if (!isValidDay(value as string)) {
          return;
        }
        break;
      case 'size':
        if (!isValidSize(value as SizeData)) {
          return;
        }
        break;
      case 'workType':
        if (!isValidWorkType(value as string)) {
          return;
        }
        break;
      case 'price':
        if (!isValidPrice(value as string)) {
          return;
        }
        break;
      case 'country':
        if (!isValidCountry(value as string)) {
          return;
        }
        break;
      default:
        break;
    }
    return true;
  }

  const isEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email)) {
      return false;
    }
    return true;
  };

  const isValidAui = (AUI: string): boolean => {
    const auiPattern = /^[a-f0-9]{8}$/;
    const onlyDigitsPattern = /^[0-9]+$/;
    const onlyLettersPattern = /^[a-f]+$/;
    if (!AUI) return false;
    if (onlyDigitsPattern.test(AUI) || onlyLettersPattern.test(AUI)) return false;
    return auiPattern.test(AUI);
  };

  const isValidHandle = (handle: string): boolean => {
    if (!handle) return false;
    if (/\s/.test(handle)) return false;
    if (!HANDLE_REGEX.test(handle)) return false;
    const key = handle.toLowerCase();
    if (RESERVED_HANDLES.has(key)) return false;
    return true;
  };

  const validateHandle = (value: string): string | null => {
    if (value.length === 0) {
      return null;
    }
    
    // 공백 체크
    if (/\s/.test(value)) {
      return getHintMessage('VALIDATION_HANDLE_SPACES');
    }
    
    // 형식 체크 (소문자/숫자로 시작, 언더스코어로 구분 가능)
    if (!/^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(value)) {
      return getHintMessage('VALIDATION_HANDLE_FORMAT');
    }

    // 길이 체크 (3-30자)
    if (value.length < 3 || value.length > 30) {
      return getHintMessage('VALIDATION_HANDLE_LENGTH');
    }
    
    // 예약된 단어 체크
    const lowerValue = value.toLowerCase();
    if (RESERVED_HANDLES.has(lowerValue)) {
      return getHintMessage('VALIDATION_HANDLE_RESERVED');
    }
    
    return null;
  };

  /** 입력란용: 첫 번째 문제만 짧은 힌트 (빈 문자열은 null). */
  const getTagNameInputHint = (value: string): string | null => {
    if (value.length === 0) {
      return null;
    }
    if (value !== value.trim()) {
      return getHintMessage('VALIDATION_TAG_NAME_TRIM');
    }
    const t = value.trim();
    if ([...t].length > INPUT_TAG_NAME_MAX_LENGTH) {
      return getHintMessage('VALIDATION_TAG_NAME_LENGTH', { maxLength: INPUT_TAG_NAME_MAX_LENGTH });
    }
    if (/[^\p{L}\p{N} \-]/u.test(t)) {
      return getHintMessage('VALIDATION_TAG_NAME_INVALID_CHARS');
    }
    if (/^[ \-]|[ \-]$/.test(t)) {
      return getHintMessage('VALIDATION_TAG_NAME_EDGE_SEPARATOR');
    }
    if (/\s{2,}/.test(t)) {
      return getHintMessage('VALIDATION_TAG_NAME_DOUBLE_SPACE');
    }
    if (/--/.test(t)) {
      return getHintMessage('VALIDATION_TAG_NAME_DOUBLE_HYPHEN');
    }
    if (!/^[\p{L}\p{N}]+(?:[ \-][\p{L}\p{N}]+)*$/u.test(t)) {
      return getHintMessage('VALIDATION_TAG_NAME_BETWEEN_WORDS');
    }
    return null;
  };

  const validateTagNameInput = (value: string): string | null => getTagNameInputHint(value);

  // password validation hard version
  // 일반적인 비밀번호 금지 목록 (HashSet 사용으로 O(1) 검색)
  const COMMON_PASSWORDS = new Set([
    // ① 패턴·반복·숫자 연속
    '123456789012345', 'qwertyuiopasdfg', 'passwordpassword',
    'letmeinletmein!', 'iloveyouforever1', 'aaaaaaaaaaaaaaaa',
    'baselinebaseline', 'springsecurity21', 'springspringspring',
    'supermanbatmanlol', 'opensesameopenses', 'trustnostranger42',
    'correcthorsebattery', 'footballfootball1', 'qazwsxedcrfvtgby',
    'p4ssw0rdp4ssw0rd',

    // ② 서비스명 변형(피싱·사전 공격 방어)
    'architravearchi', 'architrave2025!', 'architrave12345', 'architrave!@#$%',

    // ③ 15 자 이상 '바보 비번' Top-10 (★ 신규 추가 ★)
    '1q2w3e4r5t6y7u8i', 'password123456789', '1234567890123456',
    'qwertyuiopasdfgh', '12345678password', 'unitedstatesofamerica',
    '1111111111111111', '12345678901234567890', 'abcdefghijklmnop'
  ]);

  const isValidPasswordLength = (password: string) => {
    // 최소 15자, 최대 128자
    if (password.length < 15 || password.length > 64) {
      return false;
    }

    return true;
  };

  const isValidPasswordAntiPattern = (password: string) => {
    // 일반적인 비밀번호 금지 (HashSet 사용으로 O(1) 검색)
    if (COMMON_PASSWORDS.has(password.toLowerCase())) {
      return false;
    }

    return true;
  };


  const isValidVideoUrl = (url: string): boolean => {
    if (url === '' || url == null) return true;

    if (!parseSupportedVideoUrl(url)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_VIDEO_URL_FORMAT'),
      });
      return false;
    }

    return true;
  };

  const isValidTextBlockLinkUrl = (url: string): boolean => {
    if (url === '' || url == null) return true;

    if (!parseSupportedTextBlockLinkUrl(url)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_TEXT_BLOCK_LINK_URL_FORMAT'),
      });
      return false;
    }

    return true;
  };

  const isValidPasswordContinuous = (password: string) => {
    // 연속된 문자 금지 (3자 이상)
    for (let i = 0; i < password.length - 2; i++) {
      const char1 = password.charCodeAt(i);
      const char2 = password.charCodeAt(i + 1);
      const char3 = password.charCodeAt(i + 2);

      if (char2 === char1 + 1 && char3 === char2 + 1) {
        return false;
      }
    }

    // 반복된 문자 제한 (3자 이상)
    for (let i = 0; i < password.length - 2; i++) {
      if (password[i] === password[i + 1] && password[i] === password[i + 2]) {
        return false;
      }
    }

    return true;
  };

  // password validation mild version
  const PASSWORD_ASCII_SYMBOL_REGEX = /[!@#$%^&*()_+\-=\[\]{}|;':",.<>/?`~\\]/;
  const PASSWORD_REGEX = /^(?=.{8,20}$)(?!.*\s)(?!.*[\x00-\x1F\x7F-\x9F])(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{}|;':",.<>/?`~\\]).+$/;

  const explainPasswordIssues = (value: string): string[] => {
    const issues: string[] = [];
    if (value.length < 8 || value.length > 20) issues.push(getAlertMessage('PASSWORD_LENGTH_REQUIRED'));
    if (!/[a-z]/.test(value)) issues.push(getAlertMessage('PASSWORD_NEED_LOWERCASE'));
    if (!/[A-Z]/.test(value)) issues.push(getAlertMessage('PASSWORD_NEED_UPPERCASE'));
    if (!/\d/.test(value)) issues.push(getAlertMessage('PASSWORD_NEED_NUMBER'));
    if (!PASSWORD_ASCII_SYMBOL_REGEX.test(value)) issues.push(getAlertMessage('PASSWORD_NEED_SPECIAL'));
    if (/\s/.test(value)) issues.push(getAlertMessage('PASSWORD_NO_SPACES'));
    return issues;
  };


  return {
    checkType,
    isValidVideoUrl,
    isValidTextBlockLinkUrl,
    isEmail,
    isValidAui,
    isValidHandle,
    validateHandle,
    validateTagNameInput,
    isValidPasswordLength,
    isValidPasswordAntiPattern,
    isValidPasswordContinuous,
    PASSWORD_REGEX,
    explainPasswordIssues,
  };
};
